import { OrganisationProductSummary } from "@fero/components";
import {
  MINIMUM_SEARCH_TERM_LENGTH,
  useGetProductSalePricesQuery,
  useProductSearchQuery,
} from "@fero/hooks";
import type { SelectedProduct } from "@fero/stores";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  CircularProgress,
  InputAdornment,
  ListItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { WithDbId } from "adl-gen/common/db";
import type { Product } from "adl-gen/ferovinum/app/db";
import React, { type SyntheticEvent, useCallback, useState } from "react";
import { useGetStock } from "../../hooks/use-get-stock";
import { useTradeSaleHelpers } from "../hooks";

interface ProductSearchBarProps {
  onSelectProduct: (product: SelectedProduct) => void;
  selectedProductCodes: Set<string>;
  producibles: Map<string, string>;
}

const NoOptionsText = () => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Typography variant="body1">{"Product could not be found."}</Typography>
  </Stack>
);

const AutocompleteInput: React.FC<
  AutocompleteRenderInputParams & { isLoading: boolean }
> = ({ isLoading, InputProps, ...props }) => (
  <TextField
    {...props}
    label="Search by product code, name or producer"
    fullWidth
    InputProps={{
      ...InputProps,
      startAdornment: (
        <InputAdornment position="start">
          <SearchOutlinedIcon />
        </InputAdornment>
      ),
      endAdornment: (
        <>
          {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
          {InputProps.endAdornment}
        </>
      ),
    }}
  />
);

interface ProductSearchBarOptionProps {
  product: WithDbId<Product>;
  onClick: (product: SelectedProduct) => void;
  producibles: Map<string, string>;
}

const ProductSearchBarOption: React.FC<ProductSearchBarOptionProps> = (
  props,
) => {
  const { product, onClick, producibles } = props;
  const productId = product.id;
  const { code, alcoholByVolumePc, countryOfOrigin } = product.value;

  const {
    getSalePrice,
    nominatedPurchaserId,
    nominatedPurchaser,
    storageLocationId,
  } = useTradeSaleHelpers();

  const { aggregatedStock, isLoading } = useGetStock(
    nominatedPurchaser.organisationId,
    storageLocationId,
  );

  const quantities = aggregatedStock.get(productId);

  const { data: productSalePrices, isLoading: isLoadingProductSalePrices } =
    useGetProductSalePricesQuery({
      nominatedPurchaserId,
      enabled: !!nominatedPurchaserId,
    });
  const productSalePrice = productSalePrices?.find(
    (p) => p.productCode === code,
  );
  const producibleQuantity = producibles.get(productId) ?? "0";
  const onSelectProduct = () => {
    const availableQuantity = quantities ? quantities.value : 0;
    const isCaskProduct = product.value.unitType === "cask";
    const initialQuantity = isCaskProduct ? availableQuantity : 0;
    const salePrice = getSalePrice(productSalePrice) ?? 0;
    const initialSubtotal = initialQuantity * salePrice;

    onClick({
      productId,
      summary: product,
      available: availableQuantity,
      producible: Number.parseInt(producibleQuantity, 10) ?? 0,
      subtotal: initialSubtotal,
      quantity: initialQuantity,
      salePrice,
      lineItemDiscount: 0,
      freeUnits: 0,
      attributeSelection: {},
    });
  };

  if (isLoading || isLoadingProductSalePrices) {
    return (
      <ListItem>
        <Skeleton animation="wave" variant="rounded" height={70} width="100%" />
      </ListItem>
    );
  }

  return (
    <React.Fragment key={productId}>
      <ListItem {...props} sx={{ pl: 4 }} onClick={onSelectProduct}>
        <Box sx={{ p: 2 }}>
          <OrganisationProductSummary
            {...props.product.value}
            abv={alcoholByVolumePc}
            country={countryOfOrigin}
          />
        </Box>
      </ListItem>
    </React.Fragment>
  );
};

/**
 * A new product search bar component making use of react query
 */
export const ProductSearchBar: React.FC<ProductSearchBarProps> = (props) => {
  const { onSelectProduct, selectedProductCodes, producibles } = props;

  const [inputValue, setInputValue] = useState("");
  const [selectedValue, setSelectedValue] = useState<WithDbId<Product> | null>(
    null,
  );
  const { isFetching, data } = useProductSearchQuery(inputValue);

  const isAutocompleteShown =
    inputValue.trim().length >= MINIMUM_SEARCH_TERM_LENGTH;
  const filterOptions = useCallback(
    (products: WithDbId<Product>[]) =>
      products.filter((p) => !selectedProductCodes.has(p.value.code)),
    [selectedProductCodes],
  );

  return (
    <Autocomplete<WithDbId<Product>>
      filterOptions={filterOptions}
      value={selectedValue}
      inputValue={inputValue}
      noOptionsText={<NoOptionsText />}
      getOptionLabel={(p) => p.value.name}
      loading={isFetching}
      renderInput={(args) => (
        <AutocompleteInput {...args} isLoading={isFetching} />
      )}
      options={data || []}
      onInputChange={(_event: SyntheticEvent, newInputValue: string) => {
        setInputValue(newInputValue);
      }}
      open={isAutocompleteShown}
      renderOption={(props, product) => (
        <ProductSearchBarOption
          {...props}
          product={product}
          producibles={producibles}
          onClick={(product) => {
            if (product) {
              // Clear the selected value immediately so that a user can easily start typing again
              setSelectedValue(null);
              setInputValue("");

              // Let any parent components know which product was selected
              onSelectProduct(product);
            }
          }}
        />
      )}
    />
  );
};
